import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { ShieldCheck, Warning, LockKey, CircleNotch } from "@phosphor-icons/react";

export default function LockAccount() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("idle"); // idle | working | done | error
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState("");

  useEffect(() => { if (!token) setState("error"); }, [token]);

  const lockAcct = async () => {
    setState("working");
    try {
      const { data } = await api.post("/auth/lock-account", { token });
      setEmail(data.email || "");
      setState("done");
    } catch (e) {
      setMsg(formatErr(e?.response?.data?.detail) || e.message);
      setState("error");
    }
  };

  return (
    <div className="min-h-screen flex bg-[#F9F7F4]">
      <div className="flex-1 grid place-items-center p-8">
        <div className="w-full max-w-md card !p-10" data-testid="lock-account-card">
          {state === "done" ? (
            <div className="text-center" data-testid="lock-done">
              <div className="w-14 h-14 rounded-full bg-[#E2F3EB] grid place-items-center mx-auto">
                <ShieldCheck size={28} weight="fill" className="text-[#2D7A5D]" />
              </div>
              <h1 className="font-display text-2xl font-medium tracking-tight text-[#132A13] mt-5">Account locked</h1>
              <p className="text-sm text-[#4F5D4F] mt-3">
                All active sessions have been revoked{email ? <> for <span className="font-medium text-[#132A13]">{email}</span></> : ""}. Anyone signed in with your credentials — including the attacker — has been kicked out.
              </p>
              <p className="text-sm text-[#4F5D4F] mt-4">
                To reclaim access, request a new password-reset link using your email.
              </p>
              <Link to="/forgot-password" className="btn-primary mt-6" data-testid="reclaim-account">Reclaim my account</Link>
            </div>
          ) : state === "error" ? (
            <div className="text-center" data-testid="lock-error">
              <div className="w-14 h-14 rounded-full bg-[#FBE3E3] grid place-items-center mx-auto">
                <Warning size={28} weight="fill" className="text-[#C93B3B]" />
              </div>
              <h1 className="font-display text-2xl font-medium tracking-tight text-[#132A13] mt-5">Link can&apos;t be used</h1>
              <p className="text-sm text-[#4F5D4F] mt-3">{msg || "This lock link is missing, expired, or already used."}</p>
              <p className="text-xs text-[#4F5D4F] mt-4">
                If you still think your account is compromised, reset your password now to invalidate the attacker&apos;s access.
              </p>
              <Link to="/forgot-password" className="btn-primary mt-6" data-testid="fallback-reset">Reset password instead</Link>
            </div>
          ) : (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-[#FBE3E3] grid place-items-center mx-auto">
                <LockKey size={28} weight="fill" className="text-[#C93B3B]" />
              </div>
              <h1 className="font-display text-2xl font-medium tracking-tight text-[#132A13] mt-5">Lock this account?</h1>
              <p className="text-sm text-[#4F5D4F] mt-3 leading-relaxed">
                This will immediately revoke every active session, invalidate any pending password-reset links, and force whoever is currently signed in to be logged out. You&apos;ll reclaim access via the standard forgot-password flow.
              </p>
              <button
                onClick={lockAcct}
                disabled={state === "working"}
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C93B3B] hover:bg-[#a93030] text-white font-semibold text-sm transition-colors disabled:opacity-50"
                data-testid="confirm-lock-button"
              >
                {state === "working" ? <><CircleNotch className="animate-spin" size={16} /> Locking…</> : <><LockKey size={16} weight="fill" /> Lock account now</>}
              </button>
              <p className="text-xs text-[#4F5D4F] mt-6">
                Wasn&apos;t you who reset the password? This is the right button.<br />
                Made a mistake? <Link to="/login" className="text-[#F2B632] hover:underline">Go back to sign in</Link>.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
